import { useMutation, useQuery } from 'react-query';
import { AxiosError } from 'axios';
import axiosRetry from 'axios-retry';

import { axios } from 'configurations/axios';
import { useSessionStore } from 'session/useSessionStore';
import { LineType } from 'features/seismic-3d/models/enums/LineType';
import { usePerformanceMonitoringStore } from 'features/seismic/stores/usePerformanceMonitoringStore';
import { IResponseAPI } from 'models/interfaces/IResponseAPI';
import { IErrorResponse } from 'features/seismic/models/interfaces/IErrorResponse';
import { getParamsFromUrl } from 'features/seismic/utils/seismicUrlUtils';
import { useSnackbarStore } from 'components/geopostSnackbar/store/useSnackbarStore';
import { CacheSystem } from 'features/seismic/models/enums/CacheSystem';
import { SeismicVolumeDefaultsDTO } from 'features/seismic/models/interfaces/DefaultScaleDTO';
import { FrequencySpectrum } from 'features/seismic/models/types/FrequencySpectrum';

export type RenderRequest = {
    sourceApi: string,
    lineType: LineType,
    lineNumber: number,
    volumeToken: string,
    surveyLineStart: number,
    surveyLineEnd: number,
    lineStartSample: number,
    lineEndSample: number,
    skipTraceCount: number,
    sampleInterval: number,
    filePath: string,
    byteSize: number,
    volumeSamplesPerTrace: number,
    lineIncrement: number,
    traceRangeStart: number,
    traceRangeEnd: number,
    parallelProcesses: number,
    surveyInlineIncrement: number,
    surveyXlineIncrement: number,
    isIndexedVolume: boolean,
    surveyInlineEnd: number,
    surveyInlineStart: number,
    surveyXlineStart: number,
    surveyXlineEnd: number,
    inlineDimensionName: string,
    xlineDimensionName: string,
    sampleDimensionName: string,
    cdpDimensionName: string,
};

export type PrepareTilesRenderRequest = RenderRequest & {
    prepareLineStart: number,
    prepareLineEnd: number,
    cacheSystem: CacheSystem,
    storeGainCache: boolean,
    attributeCalculationMethod: number,
};

export type GetFrequencySpectrumRequest = RenderRequest & {
    lineStart: number,
    lineEnd: number,
    sortOption: string,
};

export type SamplesReference = {
    MinSampleValue: number,
    MaxSampleValue: number
};

export const usePrepareTilesRender = () => {
    const host = useSessionStore(state => state.tenantConfiguration?.endpoints.prepareRender);
    const addPrepareRenderPerformanceData = usePerformanceMonitoringStore(state => state.addPrepareRenderPerformanceData);

    const { showSnackbar } = useSnackbarStore(state => ({
        showSnackbar: state.showSnackbar
    }));
    const urlParams = getParamsFromUrl();
    return useMutation({
        mutationFn: async (data: PrepareTilesRenderRequest) : Promise<SamplesReference> => {
            const response = await axios.post<SamplesReference>(
                host + '/seismic/render/prepareRender',
                data,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    'axios-retry': {
                        retries: 3,
                        retryCondition: () => true
                    },
                    timeout: 120000
                }
            );

            if (urlParams.performanceMonitoring){
                const getSampleTime = response.headers['X-Gp-Debug-Api-Get-Sample-Time'.toLowerCase()];
                const getGenerateCacheTime = response.headers['X-Gp-Debug-Api-Get-Generate-Cache-Time'.toLowerCase()];
                const note = response.headers['X-Gp-Debug-Api-Get-Generate-Note'.toLowerCase()];

                addPrepareRenderPerformanceData({
                    getGenerateCacheTime: getGenerateCacheTime,
                    getSampleTime: getSampleTime,
                    notes: note,
                    error: '-'
                });
            }

            return response.data;
        },
        retry: 3,
        onError: (error) => {
            let errorMessage = 'Error on get samples.';

            if (urlParams.performanceMonitoring){
                let errorMessagePrepareRender = '';

                let getSampleTime = '';
                let getGenerateCacheTime = '';
                let note = '';

                if (error instanceof AxiosError) {
                    const axiosError = error as AxiosError<IResponseAPI<IErrorResponse>>;

                    if (axiosError.response && axiosError.response.data && axiosError.response.data.Result){
                        getSampleTime = axiosError.response.headers['X-Gp-Debug-Api-Get-Sample-Time'.toLowerCase()];
                        getGenerateCacheTime = axiosError.response.headers['X-Gp-Debug-Api-Get-Generate-Cache-Time'.toLowerCase()];
                        note = axiosError.response.headers['X-Gp-Debug-Api-Get-Generate-Note'.toLowerCase()];

                        const errorResponse = axiosError.response.data.Result;
                        errorMessagePrepareRender = `${errorResponse['ErrorName']} - ${errorResponse.Error} - ${errorResponse.Traceback}`;

                        const messageLower = errorResponse.Error.toLowerCase();
                        if (messageLower.indexOf('discern access pattern') > -1 || messageLower.indexOf('permission error') > -1){
                            errorMessage = errorResponse.Error;
                        }
                    }
                }
                if (!errorMessagePrepareRender) {
                    let commonError = error as Error;
                    let stack = (commonError.stack)?` - ${commonError.stack}`:'';
                    errorMessagePrepareRender = commonError.message + stack;
                }

                addPrepareRenderPerformanceData({
                    getGenerateCacheTime: getGenerateCacheTime,
                    getSampleTime: getSampleTime,
                    notes: note,
                    error: errorMessagePrepareRender
                });
            }

            showSnackbar('error', errorMessage);
        }
    });
};

export const useSaveDefaultScale = () => {
    const host = useSessionStore(state => state.tenantConfiguration?.endpoints.metadata);
    const jwt = useSessionStore(state => state.jwtToken);

    return useMutation({
        mutationFn: async (data: SeismicVolumeDefaultsDTO) => {
            const response = await axios.post(host + '/Seismic/Volume/SaveDefaultScale', data, {
                headers: {
                    Authorization: 'Bearer ' + jwt
                }
            });
            return response.data.Result;
        }
    });
};

export const useGetFrequencySpectrum = () => {
    const [ jwt, host ] = useSessionStore(state => [state.jwtToken, state.tenantConfiguration?.endpoints.frequencySpectrum]);

    return useMutation(async (params: GetFrequencySpectrumRequest) => {

        const response = await axios.post<IResponseAPI<FrequencySpectrum[]>>(
            host + '/Seismic/GetFrequencySpectrum',
            params,
            {
                headers: {
                    Authorization: 'Bearer ' + jwt
                }
            }
        );
        return response.data.Result;
    });
};